import { format } from 'date-fns';
import { AxiosResponse } from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  findConciliationDetailRequestMap,
  findConciliationDetailResponseMap,
  findConciliationHeaderRequestMap,
  findConciliationHeaderResponseMap,
  findMastersImportRequestMap,
  findMastersImportResponseMap,
  findMastersImportExecutionRequestMap,
  findFiscalDetailRequestMap,
  findFiscalDetailResponseMap,
  getCountriesForPermissions,
  countConciliationDetailErrors,
  countConciliationHeaderStates,
} from '../../helpers';
import {
  FindConciliationDetailResponse,
  FindConciliationHeaderResponse,
  FindMastersImportResponse,
  FindMastersTypesResponse,
  FindFiscalDetailResponse,
  ConciliationApi,
  GetPosResponse,
} from '../../services';
import {
  HttpRequestError,
  ConciliationHeaderState,
  ConciliationDetailErrors,
  ReportFilter,
  FranchiseType,
  ConciliationHeaderId,
  MastersImportDetailFilter,
  MasterData,
  FiscalDetailFilter,
  Permission,
  AppPermissions,
  DateValue,
} from '../../types';
import {
  reportFilterConciliationHeaderStateSelector,
  reportFilterConciliationDetailErrorsSelector,
  reportFilterSelector,
  reportFranchiseTypeFilterSelector,
  reportFilterFederativeEntitySelector,
  conciliationHeaderIdSelector,
  reportMastersImportFilterSelector,
  masterDataDataSelector,
  reportFiscalDetailFilterSelector,
  userPermissionsDataSelector,
  activeCountriesSelector,
  reportConciliationDetailFilterStoresSelector,
  reportConciliationDetailFilterStartDateSelector,
  reportConciliationDetailFilterEndDateSelector,
  reportFilterCountryCodeSelector,
  reportOpStructuresFilterSelector,
} from '../selectors';
import {
  findConciliationDetailFailure,
  findConciliationDetailSuccess,
  findConciliationHeaderFailure,
  findConciliationHeaderSuccess,
  findMastersImportSuccess,
  findMastersImportFailure,
  findMastersTypesFailure,
  findMastersTypesSuccess,
  mastersImportExecutionFailure,
  mastersImportExecutionSuccess,
  findFiscalDetailSuccess,
  findFiscalDetailFailure,
  findPosSuccess,
  findPosFailure,
} from './actionCreators';
import {
  ActionType,
  FindConciliationDetailRequestedAction,
  FindConciliationHeaderRequestedAction,
  MastersImportExecutionRequestedAction,
} from './types';

import { enqueueNotification } from '..';

function* notifyOnedriveSuccess() {
  yield put(
    enqueueNotification({
      key: 'sendOnedriveSuccess',
      message: t => t('shared.messages.sendToOnedrive'),
      variant: 'success',
    })
  );
}

function* findConciliationDetail(
  action: FindConciliationDetailRequestedAction
): Generator<any, void, any> {
  const conciliationHeaderId: ConciliationHeaderId = yield select(conciliationHeaderIdSelector);
  const filters: ReportFilter = yield select(reportFilterSelector);
  const errorsFilter: ConciliationDetailErrors[] = yield select(
    reportFilterConciliationDetailErrorsSelector
  );
  const opStructuresFilter = yield select(reportOpStructuresFilterSelector);

  try {
    const { data, status }: AxiosResponse<FindConciliationDetailResponse[]> = yield call(
      ConciliationApi.findConciliationDetail,
      findConciliationDetailRequestMap(
        filters,
        errorsFilter,
        conciliationHeaderId,
        action.sendToOndrive,
        opStructuresFilter
      )
    );
    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findConciliationDetailSuccess([], countConciliationDetailErrors([])));
    } else {
      const mappedData = findConciliationDetailResponseMap(data);
      yield put(
        findConciliationDetailSuccess(mappedData, countConciliationDetailErrors(mappedData))
      );
    }
  } catch (error) {
    yield put(findConciliationDetailFailure(error as HttpRequestError));
  }
}

function* findConciliationHeader(
  action: FindConciliationHeaderRequestedAction
): Generator<any, void, any> {
  const filters: ReportFilter = yield select(reportFilterSelector);
  const stateSelected: ConciliationHeaderState[] = yield select(
    reportFilterConciliationHeaderStateSelector
  );

  const franchiseTypes: FranchiseType[] = yield select(reportFranchiseTypeFilterSelector);
  const federativeEntities: string[] = yield select(reportFilterFederativeEntitySelector);
  const opStructuresFilter = yield select(reportOpStructuresFilterSelector);
  try {
    const { data, status }: AxiosResponse<FindConciliationHeaderResponse[]> = yield call(
      ConciliationApi.findConciliationHeader,
      findConciliationHeaderRequestMap(
        filters,
        stateSelected,
        franchiseTypes,
        federativeEntities,
        action.sendToOndrive,
        opStructuresFilter
      )
    );

    if (status === 202) {
      yield notifyOnedriveSuccess();
      yield put(findConciliationHeaderSuccess([], countConciliationHeaderStates([])));
    } else {
      const mappedData = findConciliationHeaderResponseMap(data);
      yield put(
        findConciliationHeaderSuccess(mappedData, countConciliationHeaderStates(mappedData))
      );
    }
  } catch (error) {
    yield put(findConciliationHeaderFailure(error as HttpRequestError));
  }
}

function* mastersImportExecutionSuccessNotification() {
  yield put(
    enqueueNotification({
      key: 'mastersImportExecutionSucces',
      message: t => t('masters-import.execution.message-success'),
      variant: 'success',
    })
  );
}

function* findMastersImport() {
  const filters: MastersImportDetailFilter = yield select(reportMastersImportFilterSelector);
  const { countries }: MasterData = yield select(masterDataDataSelector);
  try {
    const { data }: AxiosResponse<FindMastersImportResponse> = yield call(
      ConciliationApi.findMastersImport,
      findMastersImportRequestMap(filters)
    );
    yield put(findMastersImportSuccess(findMastersImportResponseMap(data, countries)));
  } catch (error) {
    yield put(findMastersImportFailure(error as HttpRequestError));
  }
}

function* findMastersTypes() {
  try {
    const { data }: AxiosResponse<FindMastersTypesResponse> = yield call(
      ConciliationApi.findMastersTypes
    );
    yield put(findMastersTypesSuccess(data));
  } catch (error) {
    yield put(findMastersTypesFailure(error as HttpRequestError));
  }
}

function* mastersImportExecution({ payload }: MastersImportExecutionRequestedAction) {
  try {
    yield call(
      ConciliationApi.mastersImportExecution,
      findMastersImportExecutionRequestMap(payload)
    );
    yield put(mastersImportExecutionSuccess());
  } catch (error) {
    yield put(mastersImportExecutionFailure(error as HttpRequestError));
  }
}

function* findFiscalDetail() {
  try {
    const filters: FiscalDetailFilter = yield select(reportFiscalDetailFilterSelector);
    const permissions: Permission[] = yield select(userPermissionsDataSelector);
    const { data: countries } = yield select(activeCountriesSelector);
    const authorizedCountries = getCountriesForPermissions(
      [AppPermissions.CONCILIATIONS_DETAILS],
      permissions,
      filters.countries.length ? filters.countries : countries
    ).map(i => i?.countryCode);
    if (!authorizedCountries) return;

    const { data }: AxiosResponse<FindFiscalDetailResponse> = yield call(
      ConciliationApi.findSovosDetail,
      findFiscalDetailRequestMap(filters, authorizedCountries)
    );
    yield put(findFiscalDetailSuccess(findFiscalDetailResponseMap(data)));
  } catch (error) {
    yield put(findFiscalDetailFailure(error as HttpRequestError));
  }
}

function* findPos() {
  try {
    const countryCode: string = yield select(reportFilterCountryCodeSelector);
    const stores: { storeAcronym: string }[] = yield select(
      reportConciliationDetailFilterStoresSelector
    );

    const start: DateValue = yield select(reportConciliationDetailFilterStartDateSelector);
    const end: DateValue = yield select(reportConciliationDetailFilterEndDateSelector);

    const storeAcronym = stores?.[0]?.storeAcronym;
    const startDate: Date | null = start?.accept ?? null;
    const endDate: Date | null = end?.accept ?? null;

    if (!countryCode || !storeAcronym || !startDate || !endDate) {
      yield put(findPosSuccess([]));
      return;
    }

    const accountingDateRange = `${format(startDate, 'yyyy-MM-dd')},${format(
      endDate,
      'yyyy-MM-dd'
    )}`;

    const { data }: AxiosResponse<GetPosResponse> = yield call(ConciliationApi.getPos, {
      countryAcronym: countryCode,
      storeAcronym,
      accountingDateRange,
    });

    yield put(findPosSuccess((data ?? []).map(String)));
  } catch (error) {
    yield put(findPosFailure(error as HttpRequestError));
  }
}

export default [
  takeLatest(ActionType.findMastersImportRequested, findMastersImport),
  takeLatest(ActionType.findMastersTypesRequested, findMastersTypes),
  takeLatest(ActionType.mastersImportExecutionRequested, mastersImportExecution),
  takeLatest(ActionType.findFiscalDetailRequested, findFiscalDetail),
  takeLatest(ActionType.findConciliationHeaderRequested, findConciliationHeader),
  takeLatest(ActionType.findConciliationDetailRequested, findConciliationDetail),
  takeLatest(ActionType.mastersImportExecutionSuccess, mastersImportExecutionSuccessNotification),
  takeLatest(ActionType.findPosRequested, findPos),
];
